import { useCookieConsentStore } from "~/layers/base/stores/cookieConsentStore";

type CookiebotWindow = Window & {
	Cookiebot?: { consent?: Record<string, boolean> };
};

/**
 * Cookiebot → base consent store bridge
 *
 * breadandboxers uses Cookiebot as its CMP (`cmp: "cookiebot"`), so the custom
 * consent Banner never renders and the base store's `marketing`/`statistics`/
 * `preferences` flags would otherwise stay `false` forever. This plugin mirrors
 * Cookiebot's decision into the base store so `useCookieConsent()` is the single
 * source of truth on every template (AGENTS.md rule #4) — which is what lets
 * `useMarketingConsent()`-gated code (Clarity) and `Video.vue` work here.
 *
 * It only syncs state; it does NOT persist (Cookiebot owns its own cookie) or
 * dispatch Google Consent Mode (Cookiebot's `data-framework: "gtm"` already
 * does). Scripts that gate via Cookiebot directly (Optimizely text/plain,
 * Dotdigital) are unaffected.
 */
export default defineNuxtPlugin(() => {
	const store = useCookieConsentStore();
	const w = window as CookiebotWindow;

	const sync = () => {
		const consent = w.Cookiebot?.consent;
		if (!consent) return;
		store.syncFromCmp({
			statistics: Boolean(consent.statistics),
			marketing: Boolean(consent.marketing),
			preferences: Boolean(consent.preferences)
		});
	};

	window.addEventListener("CookiebotOnConsentReady", sync);
	window.addEventListener("CookiebotOnAccept", sync);
	window.addEventListener("CookiebotOnDecline", sync);

	// Consent may already be resolved before this plugin runs (returning visitor,
	// or the event fired during hydration) — sync the current state immediately.
	if (w.Cookiebot?.consent) {
		sync();
	}

	// Dev/HMR: drop listeners when this module is hot-replaced so they don't
	// accumulate across reloads. No-op (tree-shaken) in production.
	if (import.meta.hot) {
		import.meta.hot.dispose(() => {
			window.removeEventListener("CookiebotOnConsentReady", sync);
			window.removeEventListener("CookiebotOnAccept", sync);
			window.removeEventListener("CookiebotOnDecline", sync);
		});
	}
});
